/**
 * How a phone number is WRITTEN — ONE definition, used by every export, by the
 * live sheet feed, and by the portal screens that show a number.
 *
 * One rule for both, deliberately: a host reading a mobile off the Children
 * table and the same mobile in their spreadsheet must see the same thing, or
 * they will wonder which one is right.
 *
 * THE PROBLEM. `0412345678` is ten digits and nothing else, so Excel, Google
 * Sheets and Numbers all read it as a NUMBER — and a number has no leading
 * zero, so the cell shows `412345678`. Every Australian mobile in the file
 * loses its first digit, silently, and a host ringing a family from the
 * spreadsheet dials a number that cannot connect. There is no CSV flag that
 * prevents this: a bare run of digits is a number wherever it lands.
 *
 * THE FIX. Group the number the way it is written in Australia —
 * "0412 345 678" — which is not a valid number to any spreadsheet, so all
 * three keep it as text, zero and all. It is also the form people actually
 * read it in, and every dialler and SMS tool strips the spaces anyway.
 *
 * The alternative (a leading apostrophe, `'0412345678`) hides the number's
 * "text" marker in some tools and shows it literally in others, which means
 * the file is right in one app and visibly wrong in the next. Grouping works
 * everywhere and needs no explanation.
 *
 * ANYTHING NOT RECOGNISED IS RETURNED EXACTLY AS GIVEN. A family who typed a
 * note beside their number, an overseas number, an extension — none of them
 * get rewritten into something their owner would not recognise.
 */

/**
 * One phone number, grouped for reading — and so a spreadsheet keeps the
 * leading zero.
 *
 * Handles the shapes that turn up in the registrations:
 *  - `0412345678`      → `0412 345 678`   (mobile)
 *  - `+61412345678`    → `0412 345 678`   (the same mobile, dialled from abroad)
 *  - `0298765432`      → `02 9876 5432`   (landline with area code)
 *  - `1300123456`      → `1300 123 456`   (business number)
 *  - already spaced, overseas, or with a note → unchanged
 *
 * DISPLAY ONLY. Nothing here rewrites what is stored, and it must never be put
 * behind an input the host is typing into — a number that re-groups itself
 * mid-keystroke is unusable.
 */
export function formatPhone(raw: string | null | undefined): string {
  const text = String(raw ?? "").trim();
  if (text === "") return "";

  // Only touch numbers made of digits and the usual separators. Anything with
  // a letter in it ("0412 345 678 (mum)") is a note, and notes are left alone.
  if (!/^[+()\s\-.\d]+$/.test(text)) return text;

  const plus = text.startsWith("+");
  let digits = text.replace(/\D/g, "");
  if (digits === "") return text;

  // +61 / 0061 is the same number as a local 0 — write it the local way, so a
  // column of mobiles reads as one column rather than two conventions.
  if ((plus || digits.startsWith("61")) && digits.startsWith("61") && digits.length >= 11) {
    digits = `0${digits.slice(2)}`;
  } else if (plus) {
    // Some other country. Not ours to reformat, and never at risk of losing a
    // leading zero, because the "+" already makes the cell text.
    return text;
  }

  // 04xx / 05xx mobiles: 4-3-3.
  if (digits.length === 10 && /^0[45]/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  // Landlines with an area code: 2-4-4.
  if (digits.length === 10 && /^0[2378]/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  // 1300 / 1800: 4-3-3.
  if (digits.length === 10 && /^1[38]00/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  // 13xxxx: 2-2-2.
  if (digits.length === 6 && digits.startsWith("13")) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }
  /* An unusual shape that still STARTS WITH A ZERO is exactly the case this
     function exists for, so it does not get to fall through as a bare run of
     digits. One space is all a spreadsheet needs to treat the cell as text. */
  if (digits.startsWith("0") && !/\s/.test(text)) {
    const cut = digits.length > 6 ? 4 : 2;
    return `${digits.slice(0, cut)} ${digits.slice(cut)}`;
  }

  // No leading zero to protect (an eight-digit local landline, say) — leave it
  // exactly as the family typed it.
  return text;
}
